import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Phone, ShoppingCart, Wallet, BookOpen, AlertTriangle, RotateCw, Send } from 'lucide-react';
import { customersApi, ordersApi } from '../api';
import { formatPrice } from '../store';
import { usePermission } from '../store/auth';
import { shareLink } from '../app/share';
import { Modal } from '../components/ui/Modal';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonRows } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/ToastProvider';
import { renderOrderStatus, isOverdue } from '../components/ui/orderStatus';
import type { Customer, CustomerLedger, OrderListItem } from '../types';

/**
 * Daftardagi qaysi qatordan boshlab qarz hali ochiq ekanini topadi.
 *
 * Qoldiq ustuni — yugurib boruvchi balans, shuning uchun darhol naqd to'langan
 * buyurtma ham to'lov qatoriga qadar bir zumga qarzdek ko'rinadi. Savdogar uchun
 * bu shovqin: u yopilgan bitimning oraliq raqamini emas, hozir turgan qarzni
 * ko'rmoqchi. Balans oxirgi marta nolga tushgandan keyingi qatorlargina
 * haqiqiy qarz — qolganida qoldiq umuman ko'rsatilmaydi.
 */
const openDebtFrom = (entries: CustomerLedger['entries']): number => {
  let from = entries.length;
  for (let i = entries.length - 1; i >= 0; i--) {
    if (Number(entries[i].balance) <= 0) break;
    from = i;
  }
  return from;
};

/**
 * Mijoz kartochkasi — qarz qaysi buyurtmalardan yig'ilganini ko'rsatadi.
 *
 * Mijozlar ro'yxatidagi bitta "Qarz" raqami savolga javob bermaydi: qaysi
 * xariddan qolgan, qachon to'lanishi kerak. Shu oyna o'sha tafsilotni beradi.
 */
/** Xatoni foydalanuvchiga ham, tuzatuvchiga ham foydali qilib yozadi. */
function describeError(error: any): string {
  if (error?.code === 'ECONNABORTED') {
    return 'Server javob bermadi (vaqt tugadi). Internetni tekshirib, qayta urining.';
  }
  const status = error?.response?.status;
  if (status) return `Server xatosi (${status}).`;
  return 'Serverga ulanib bo\'lmadi.';
}

/** Xato + qayta urinish — cheksiz "yuklanmoqda" o'rniga. */
const LoadError: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="rounded-lg bg-danger-50 p-4 text-center space-y-3">
    <div className="flex items-center justify-center gap-2 text-danger-700">
      <AlertTriangle size={18} />
      <span className="font-medium">Yuklab bo'lmadi</span>
    </div>
    <p className="text-sm text-gray-600">{message}</p>
    <button onClick={onRetry} className="btn btn-secondary inline-flex items-center gap-2">
      <RotateCw size={16} />
      Qayta urinish
    </button>
  </div>
);

export const CustomerStatement: React.FC<{
  customer: Customer;
  onClose: () => void;
  onPayDebt: () => void;
}> = ({ customer, onClose, onPayDebt }) => {
  const toast = useToast();
  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'orders' | 'ledger'>('orders');
  const [ledger, setLedger] = useState<CustomerLedger | null>(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerError, setLedgerError] = useState<string | null>(null);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const openFrom = useMemo(() => (ledger ? openDebtFrom(ledger.entries) : 0), [ledger]);
  const canShare = usePermission('customers.view_ledger');
  const [sharing, setSharing] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  // Mijozga o'z hisobining ochiq havolasi — u ilovasiz, oddiy sahifa bo'lib
  // ochiladi. So'rov bosishning o'zida ketadi: `navigator.share` bosish
  // ishorasi "issiq" paytda chaqirilishi kerak.
  const handleShare = async () => {
    setSharing(true);
    try {
      const res = await customersApi.statementLink(customer.id);
      const outcome = await shareLink(res.data);
      if (outcome === 'copied') toast.success('Havola nusxalandi — mijozga yuboring');
    } catch (error) {
      console.error('Statement share error:', error);
      toast.error('Havolani yuborib bo\'lmadi');
    } finally {
      setSharing(false);
    }
  };

  const handleRevoke = async () => {
    setConfirmRevoke(false);
    try {
      await customersApi.revokeStatementLink(customer.id);
      toast.success('Oldingi havolalar o\'chirildi');
    } catch (error) {
      console.error('Statement revoke error:', error);
      toast.error('Havolani o\'chirib bo\'lmadi');
    }
  };

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setOrdersError(null);
    try {
      const res = await ordersApi.list({ customer: customer.id, page_size: 100 });
      const raw = res.data as any;
      setOrders(Array.isArray(raw) ? raw : (raw.results || []));
    } catch (error: any) {
      console.error('Customer orders fetch error:', error);
      setOrdersError(describeError(error));
      toast.error('Mijoz buyurtmalarini yuklashda xatolik');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer.id]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const fetchLedger = useCallback(() => {
    setLedgerLoading(true);
    setLedgerError(null);
    customersApi.ledger(customer.id)
      .then((res) => setLedger(res.data))
      .catch((error) => {
        console.error('Ledger fetch error:', error);
        setLedgerError(describeError(error));
      })
      .finally(() => setLedgerLoading(false));
  }, [customer.id]);

  // Daftar faqat kerak bo'lganda yuklanadi — u buyurtmalardan og'irroq.
  useEffect(() => {
    if (tab !== 'ledger' || ledger || ledgerError) return;
    fetchLedger();
  }, [tab, ledger, ledgerError, fetchLedger]);

  // Bekor qilingan va qoralama buyurtmalar hisobga kirmaydi — qarz ham
  // backend'da shu qoida bo'yicha hisoblanadi.
  const counted = orders.filter(o => o.status !== 'cancelled' && o.status !== 'draft');
  const sold = counted.reduce((s, o) => s + Number(o.total), 0);
  const paid = counted.reduce((s, o) => s + Number(o.paid_amount), 0);
  const debt = counted.reduce((s, o) => s + Number(o.debt_amount), 0);
  const unpaid = counted.filter(o => Number(o.debt_amount) > 0);

  return (
    <Modal
      open
      onClose={onClose}
      title={`${customer.name} — hisob varaqasi`}
      size="xl"
      footer={
        <>
          <button onClick={onClose} className="btn btn-secondary">Yopish</button>
          {Number(customer.debt) > 0 && (
            <button onClick={onPayDebt} className="btn btn-primary flex items-center gap-2">
              <Wallet size={18} />
              Qarzni to'lash
            </button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        {customer.phone && (
          <div className="flex items-center gap-1 text-sm text-gray-500">
            <Phone size={14} />
            {customer.phone}
          </div>
        )}

        {canShare && (
          <div className="rounded-lg border border-gray-100 p-3 space-y-2">
            <button
              onClick={handleShare}
              disabled={sharing}
              className="btn btn-primary w-full flex items-center justify-center gap-2"
            >
              <Send size={18} />
              {sharing ? 'Tayyorlanmoqda…' : 'Hisobni mijozga yuborish'}
            </button>
            <p className="text-xs text-gray-500">
              Mijoz qachon nima olgani, qancha to'lagani va qoldiq qarzini
              havola orqali o'zi ko'radi — ilova o'rnatishi shart emas.
            </p>
            {confirmRevoke ? (
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="text-gray-600">Yuborilgan havolalar ishlamay qoladi.</span>
                <button onClick={handleRevoke} className="font-medium text-danger-600">
                  O'chirish
                </button>
                <button onClick={() => setConfirmRevoke(false)} className="text-gray-500">
                  Bekor
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmRevoke(true)}
                className="text-xs text-gray-500 underline"
              >
                Oldingi havolalarni o'chirish
              </button>
            )}
          </div>
        )}

        {/* Buyurtmalar yuklanmagan bo'lsa jamlar 0 bo'lib chiqadi — bu
            "qarzi yo'q" degan noto'g'ri xulosaga olib keladi. */}
        {/* Uchta karta tor ekranda ~100px dan keladi — "322 000 UZS" bir qatorga
            sig'maydi. Shuning uchun birlik ataylab pastki qatorga chiqarilgan:
            summa o'zi hech qachon bo'linmaydi. */}
        {!ordersError && (
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-lg bg-gray-50 p-3">
            <div className="text-xs text-gray-500">Jami xarid</div>
            <div className="font-bold text-gray-900 tabular-nums whitespace-nowrap">
              {formatPrice(sold)}
            </div>
            <div className="text-[10px] font-semibold text-gray-400">UZS</div>
          </div>
          <div className="rounded-lg bg-success-50 p-3">
            <div className="text-xs text-gray-500">To'langan</div>
            <div className="font-bold text-success-600 tabular-nums whitespace-nowrap">
              {formatPrice(paid)}
            </div>
            <div className="text-[10px] font-semibold text-gray-400">UZS</div>
          </div>
          <div className={`rounded-lg p-3 ${debt > 0 ? 'bg-danger-50' : 'bg-gray-50'}`}>
            <div className="text-xs text-gray-500">Qarz</div>
            <div className={`font-bold tabular-nums whitespace-nowrap ${debt > 0 ? 'text-danger-600' : 'text-gray-900'}`}>
              {formatPrice(debt)}
            </div>
            <div className="text-[10px] font-semibold text-gray-400">UZS</div>
          </div>
        </div>
        )}

        {/* Ro'yxatdagi qarz bilan buyurtmalardan hisoblangani farq qilsa,
            buni yashirmaymiz — ma'lumot nomuvofiqligi ko'rinib tursin. */}
        {!loading && !ordersError && Math.abs(debt - Number(customer.debt)) > 0.5 && (
          <div className="rounded-lg bg-warning-50 p-3 text-sm text-warning-700">
            Diqqat: mijoz kartochkasidagi qarz {formatPrice(Number(customer.debt))} UZS,
            buyurtmalar bo'yicha esa {formatPrice(debt)} UZS.
          </div>
        )}

        {/* Ikki ko'rinish: bitimlar bo'yicha va daftar (xronologik) */}
        <div className="flex gap-2">
          <button
            onClick={() => setTab('orders')}
            className={`btn flex items-center gap-2 ${tab === 'orders' ? 'btn-primary' : 'btn-secondary'}`}
          >
            <ShoppingCart size={16} />
            Buyurtmalar
          </button>
          <button
            onClick={() => setTab('ledger')}
            className={`btn flex items-center gap-2 ${tab === 'ledger' ? 'btn-primary' : 'btn-secondary'}`}
          >
            <BookOpen size={16} />
            Daftar
          </button>
        </div>

        {tab === 'ledger' ? (
          ledgerError ? (
            <LoadError message={ledgerError} onRetry={fetchLedger} />
          ) : ledgerLoading || !ledger ? (
            <SkeletonRows />
          ) : ledger.entries.length === 0 ? (
            <EmptyState icon={BookOpen} title="Daftar bo'sh" />
          ) : (
            <>
              <p className="text-sm text-gray-500">
                <span className="font-medium">Qoldiq</span> faqat hali to'lanmagan
                qarz uchun ko'rsatiladi — yopilgan bitimlar bo'sh qoladi.
                {ledger.truncated && ' (Faqat oxirgi 500 ta yozuv)'}
              </p>
              {/* `min-w-[620px]`: busiz `w-full` jadvalni zo'rlab siqar,
                  natijada scroll o'rniga katakdagi matn o'ralib ketardi. */}
              <div className="overflow-x-auto">
                <table className="w-full min-w-[620px] text-sm tabular-nums">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Sana</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Amal</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Buyurtma</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Olindi</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase">To'landi</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Qoldiq</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.entries.map((entry, idx) => (
                      <tr key={`${entry.type}-${idx}`} className="border-t border-gray-100">
                        <td className="py-2 px-3 text-gray-500 whitespace-nowrap">
                          {new Date(entry.at).toLocaleDateString('uz-UZ')}
                        </td>
                        <td className="py-2 px-3 whitespace-nowrap">{entry.title}</td>
                        <td className="py-2 px-3 font-mono text-xs text-gray-500 whitespace-nowrap">
                          {entry.order_number}
                        </td>
                        <td className="py-2 px-3 text-right whitespace-nowrap">
                          {Number(entry.debit) > 0
                            ? <span className="text-gray-900">{formatPrice(entry.debit)}</span>
                            : <span className="text-gray-300">-</span>}
                        </td>
                        <td className="py-2 px-3 text-right whitespace-nowrap">
                          {Number(entry.credit) > 0
                            ? <span className="text-success-600">{formatPrice(entry.credit)}</span>
                            : <span className="text-gray-300">-</span>}
                        </td>
                        <td className="py-2 px-3 text-right whitespace-nowrap font-semibold">
                          {idx >= openFrom
                            ? <span className="text-danger-600">{formatPrice(entry.balance)}</span>
                            : <span className="text-gray-300 font-normal">-</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray-200 bg-gray-50 font-semibold">
                      <td className="py-2 px-3" colSpan={3}>Jami</td>
                      <td className="py-2 px-3 text-right whitespace-nowrap">
                        {formatPrice(ledger.total_taken)}
                      </td>
                      <td className="py-2 px-3 text-right whitespace-nowrap text-success-600">
                        {formatPrice(ledger.total_paid)}
                      </td>
                      <td className="py-2 px-3 text-right whitespace-nowrap">
                        {Number(ledger.balance) > 0
                          ? <span className="text-danger-600">{formatPrice(ledger.balance)}</span>
                          : <span className="text-gray-300 font-normal">-</span>}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )
        ) : ordersError ? (
          <LoadError message={ordersError} onRetry={fetchOrders} />
        ) : loading ? (
          <SkeletonRows />
        ) : counted.length === 0 ? (
          <EmptyState icon={ShoppingCart} title="Buyurtma yo'q" />
        ) : (
          <>
            {unpaid.length > 0 && (
              <p className="text-sm text-gray-500">
                To'lanmagan buyurtmalar: <span className="font-semibold">{unpaid.length}</span> ta
              </p>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Raqam</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Sana</th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Holat</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Jami</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase">To'langan</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Qarz</th>
                    <th className="text-center py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Muddat</th>
                  </tr>
                </thead>
                <tbody>
                  {counted.map((order) => {
                    const orderDebt = Number(order.debt_amount);
                    const overdue = order.due_date
                      ? isOverdue(order.due_date, Number(order.paid_amount), Number(order.total))
                      : false;
                    return (
                      <tr
                        key={order.id}
                        className={`border-t border-gray-100 ${orderDebt > 0 ? 'bg-danger-50/40' : ''}`}
                      >
                        <td className="py-2 px-3 font-mono font-medium">{order.number}</td>
                        <td className="py-2 px-3 text-gray-500">
                          {new Date(order.created_at).toLocaleDateString('uz-UZ')}
                        </td>
                        <td className="py-2 px-3 text-center">
                          {renderOrderStatus(order.status, order.status_display)}
                        </td>
                        <td className="py-2 px-3 text-right whitespace-nowrap">
                          {formatPrice(Number(order.total))}
                        </td>
                        <td className="py-2 px-3 text-right whitespace-nowrap text-success-600">
                          {formatPrice(Number(order.paid_amount))}
                        </td>
                        <td className="py-2 px-3 text-right whitespace-nowrap">
                          {orderDebt > 0 ? (
                            <span className="font-semibold text-danger-600">{formatPrice(orderDebt)}</span>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-center text-xs">
                          {order.due_date ? (
                            <span className={overdue ? 'text-danger-600 font-medium' : 'text-gray-500'}>
                              {new Date(order.due_date).toLocaleDateString('uz-UZ')}
                              {overdue && ' (o\'tgan)'}
                            </span>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
